/**
 * Shared MetricCard Component
 *
 * Reusable KPI card for displaying dashboard metrics with
 * optional color variants for status indication.
 * Supports threshold-based auto-coloring when thresholds are provided.
 * Supports optional sparkline to show trend.
 */

import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';

export type MetricCardVariant = 'default' | 'success' | 'warning' | 'error';

export interface ThresholdConfig {
  value: number;
  warningThreshold: number;
  criticalThreshold: number;
  inverse?: boolean; // If true, lower values are better (e.g., latency, error rate)
}

export interface SparklineData {
  value: number;
}

export interface MetricCardProps {
  title: string;
  value: string;
  description: string;
  isLoading: boolean;
  variant?: MetricCardVariant;
  threshold?: ThresholdConfig;
  sparklineData?: SparklineData[];
  /** Optional tooltip explaining what this metric means */
  tooltip?: string;
}

function getVariantFromThreshold(
  threshold: ThresholdConfig,
): MetricCardVariant {
  const {
    value,
    warningThreshold,
    criticalThreshold,
    inverse = true,
  } = threshold;

  if (inverse) {
    // Lower is better (latency, error rate, errors)
    if (value >= criticalThreshold) return 'error';
    if (value >= warningThreshold) return 'warning';
    return 'success';
  } else {
    // Higher is better (success rate, filter effectiveness)
    if (value <= criticalThreshold) return 'error';
    if (value <= warningThreshold) return 'warning';
    return 'success';
  }
}

const variantStyles: Record<MetricCardVariant, string> = {
  default: '',
  success: 'text-green-600 dark:text-green-400',
  warning: 'text-yellow-600 dark:text-yellow-400',
  error: 'text-red-600 dark:text-red-400',
};

const variantColors: Record<MetricCardVariant, string> = {
  default: 'var(--chart-1)',
  success: 'hsl(142, 76%, 36%)', // green-600
  warning: 'hsl(48, 96%, 53%)', // yellow-500
  error: 'hsl(0, 84%, 60%)', // red-500
};

export function MetricCard({
  title,
  value,
  description,
  isLoading,
  variant = 'default',
  threshold,
  sparklineData,
  tooltip,
}: MetricCardProps) {
  // Use threshold-based variant if threshold is provided, otherwise use manual variant
  const effectiveVariant = threshold
    ? getVariantFromThreshold(threshold)
    : variant;

  const sparklineColor = variantColors[effectiveVariant];
  const hasSparkline = sparklineData && sparklineData.length > 1;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-1">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex cursor-help"
                  aria-label={`Learn more about ${title}`}
                >
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {/* Sparkline in header */}
        {!isLoading && hasSparkline && (
          <div className="h-8 w-16">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparklineData}>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={sparklineColor}
                  fill={sparklineColor}
                  fillOpacity={0.2}
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <>
            <Skeleton className="h-8 w-24 mb-1" />
            <Skeleton className="h-4 w-32" />
          </>
        ) : (
          <>
            <div
              className={`text-2xl font-bold ${variantStyles[effectiveVariant]}`}
              role="status"
              aria-label={`${title}: ${value}`}
            >
              {value}
            </div>
            <p className="text-xs text-muted-foreground">{description}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
